import React from "react";

/**
 * ChannelIcon
 *
 * Phase 3.1 — Professional Channel Visibility
 *
 * Official platform brand icons as inline SVG.
 * Used everywhere a channel needs a visual indicator:
 *   - TicketListItemCustom (avatar badge)
 *   - ChannelBadge (header inline badge)
 *   - ChannelFilterChips (filter row)
 *   - Cross-channel activity indicators (Phase 3.4)
 *
 * Props:
 *   channel: "whatsapp" | "facebook" | "instagram" | "email" | "telegram"
 *   size:    number — icon diameter in px (default 18)
 *   style:   optional inline style override
 *
 * SVG gradient IDs:
 *   SVG gradient IDs are global in the HTML document. To prevent duplicate-ID
 *   collisions when many ChannelIcons render simultaneously (e.g. a long ticket
 *   list), we use <defs> with unique IDs derived from the component instance
 *   via React.useId() (React 18+) with a Math.random fallback for React <18.
 *   The fill="url(#...)" reference is scoped to each SVG's local defs block,
 *   so visually gradients always resolve correctly regardless of DOM order.
 */

let _idCounter = 0;
function useUniqueId() {
  // React.useId is available in React 18+; fall back to an incrementing counter.
  if (typeof React.useId === "function") {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return React.useId();
  }
  // Stable across re-renders for React <18: store on the component ref.
  // Using useState(()=>...) initializer so it runs once per mount.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [id] = React.useState(() => `ci-${++_idCounter}`);
  return id;
}

const ChannelIcon = ({ channel, size = 18, style = {} }) => {
  const uid = useUniqueId();
  const s = size;

  switch (channel) {
    // 2026-06-11 fix: Cloud-API tickets carry channel "whatsapp-api" and rendered
    // a blank icon (fell through to default: null). Reuse the WhatsApp glyph.
    // NOTE: "business-api" keeps its own dedicated teal "B" icon further down —
    // do NOT add it here or it shadows that case.
    case "whatsapp-api":
    case "whatsapp":
      return (
        <svg
          width={s}
          height={s}
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={style}
          aria-label="WhatsApp"
        >
          {/* Green circle background */}
          <circle cx="20" cy="20" r="20" fill="#25D366" />
          {/* WhatsApp speech-bubble phone path */}
          <path
            d="M20 8C13.373 8 8 13.373 8 20c0 2.138.567 4.143 1.556 5.876L8 32l6.29-1.526A11.93 11.93 0 0020 32c6.627 0 12-5.373 12-12S26.627 8 20 8z"
            fill="#25D366"
          />
          <path
            d="M20 9.5C14.201 9.5 9.5 14.201 9.5 20c0 2.013.557 3.894 1.528 5.498L9.5 30.5l5.122-1.46A10.46 10.46 0 0020 30.5c5.799 0 10.5-4.701 10.5-10.5S25.799 9.5 20 9.5z"
            fill="white"
          />
          <path
            d="M25.507 22.71c-.3-.15-1.773-.875-2.048-.974-.276-.1-.477-.15-.677.15-.2.298-.774.974-.949 1.174-.174.199-.349.224-.648.075-.3-.15-1.265-.466-2.41-1.485-.89-.794-1.492-1.774-1.667-2.073-.174-.3-.018-.461.131-.61.134-.134.299-.349.449-.523.15-.175.2-.3.3-.499.1-.2.05-.374-.025-.524-.075-.15-.677-1.636-.927-2.24-.244-.59-.492-.51-.677-.519l-.577-.01c-.2 0-.524.075-.799.374-.274.3-1.047 1.024-1.047 2.497s1.072 2.896 1.222 3.096c.15.199 2.1 3.226 5.1 4.522.713.308 1.27.491 1.703.629.715.227 1.367.195 1.882.118.574-.085 1.773-.725 2.023-1.424.25-.699.25-1.298.175-1.424-.075-.124-.274-.199-.574-.349z"
            fill="#25D366"
          />
        </svg>
      );

    case "facebook":
      return (
        <svg
          width={s}
          height={s}
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={style}
          aria-label="Facebook Messenger"
        >
          {/* Messenger gradient background */}
          <defs>
            <linearGradient id={`${uid}-msg`} x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#0062E0" />
              <stop offset="100%" stopColor="#19AFFF" />
            </linearGradient>
          </defs>
          <circle cx="20" cy="20" r="20" fill={`url(#${uid}-msg)`} />
          {/* Messenger lightning bolt / speech bubble path */}
          <path
            d="M20 8C13.373 8 8 13.149 8 19.5c0 3.49 1.713 6.604 4.39 8.647V32l4.077-2.233c1.087.3 2.24.463 3.433.463 6.627 0 12-5.149 12-11.5S26.627 8 20 8zm1.2 15.5l-3.05-3.25L12.5 23.5l6.25-6.625 3.05 3.25L27.5 16.5 21.2 23.5z"
            fill="white"
          />
        </svg>
      );

    case "instagram":
      return (
        <svg
          width={s}
          height={s}
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={style}
          aria-label="Instagram"
        >
          <defs>
            <radialGradient id={`${uid}-ig`} cx="30%" cy="107%" r="120%">
              <stop offset="0%" stopColor="#fdf497" />
              <stop offset="5%" stopColor="#fdf497" />
              <stop offset="45%" stopColor="#fd5949" />
              <stop offset="60%" stopColor="#d6249f" />
              <stop offset="90%" stopColor="#285AEB" />
            </radialGradient>
          </defs>
          <rect x="0" y="0" width="40" height="40" rx="10" fill={`url(#${uid}-ig)`} />
          {/* Camera body */}
          <rect x="10" y="12" width="20" height="17" rx="4" stroke="white" strokeWidth="2.2" fill="none" />
          {/* Lens */}
          <circle cx="20" cy="20.5" r="4.5" stroke="white" strokeWidth="2.2" fill="none" />
          {/* Flash dot */}
          <circle cx="28" cy="14" r="1.5" fill="white" />
        </svg>
      );

    case "email":
      return (
        <svg
          width={s}
          height={s}
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={style}
          aria-label="Email"
        >
          <circle cx="20" cy="20" r="20" fill="#5C6BC0" />
          <rect x="9" y="13" width="22" height="15" rx="3" stroke="white" strokeWidth="2" fill="none" />
          <path d="M9 15l11 8 11-8" stroke="white" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );

    case "telegram":
      return (
        <svg
          width={s}
          height={s}
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={style}
          aria-label="Telegram"
        >
          <circle cx="20" cy="20" r="20" fill="#29B6F6" />
          <path
            d="M29.5 11.5L8.5 19.5c-1.2.5-1.2 1.7-.2 2l5.2 1.6 2 6.2c.3.9 1.4 1.2 2 .6l2.9-2.7 5.4 4c.7.5 1.6.2 1.8-.6l3.3-17c.3-1.3-.7-2-1.4-1.6z"
            fill="white"
          />
          <path
            d="M13.5 21.5l1.5 5.5 1.8-3.5"
            stroke="#29B6F6"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );

    // WhatsApp Business API — same icon as WhatsApp (it IS WhatsApp)
    case "business-api":
      return (
        <svg
          width={s}
          height={s}
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={style}
          aria-label="WhatsApp Business"
        >
          <circle cx="20" cy="20" r="20" fill="#128C7E" />
          <path
            d="M20 9.5C14.201 9.5 9.5 14.201 9.5 20c0 2.013.557 3.894 1.528 5.498L9.5 30.5l5.122-1.46A10.46 10.46 0 0020 30.5c5.799 0 10.5-4.701 10.5-10.5S25.799 9.5 20 9.5z"
            fill="white"
          />
          <path
            d="M25.507 22.71c-.3-.15-1.773-.875-2.048-.974-.276-.1-.477-.15-.677.15-.2.298-.774.974-.949 1.174-.174.199-.349.224-.648.075-.3-.15-1.265-.466-2.41-1.485-.89-.794-1.492-1.774-1.667-2.073-.174-.3-.018-.461.131-.61.134-.134.299-.349.449-.523.15-.175.2-.3.3-.499.1-.2.05-.374-.025-.524-.075-.15-.677-1.636-.927-2.24-.244-.59-.492-.51-.677-.519l-.577-.01c-.2 0-.524.075-.799.374-.274.3-1.047 1.024-1.047 2.497s1.072 2.896 1.222 3.096c.15.199 2.1 3.226 5.1 4.522.713.308 1.27.491 1.703.629.715.227 1.367.195 1.882.118.574-.085 1.773-.725 2.023-1.424.25-.699.25-1.298.175-1.424-.075-.124-.274-.199-.574-.349z"
            fill="#128C7E"
          />
          {/* "B" badge — marks this as Business account */}
          <circle cx="30" cy="10" r="7" fill="#25D366" />
          <text x="30" y="14" textAnchor="middle" fill="white" fontSize="9" fontWeight="bold">B</text>
        </svg>
      );

    default:
      return null;
  }
};

export default ChannelIcon;
